import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { useI18n } from "i18n";
import logger from "logging/logger";
import useAnnouncementActions from "modules/announcement/hooks/useAnnouncementActions";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { extendEventEndTime } from "services/apiService/apis";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import useEventMessages from "modules/event/hooks/useEventMessages";
import {
  selectCurrentEvent,
  selectIsEventManager,
} from "modules/event/selectors";
import {
  selectAnnouncementChannel,
  selectAnnouncementSender,
} from "modules/announcement/redux/selectors";
import {
  addDialogNotification,
  closeDialogNotificationsByType,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";

/**
 * This component handles event end notifications
 * https://remoapp.atlassian.net/wiki/spaces/RCSD/pages/5046273/Event+Access+Control
 * Sends a notification to all admins 10 mins before event end time
 * Admin can extend to another 30 mins or leave it as it is.
 * For attendee
 * 1. shows notification that event will end in 10 mins.
 * 2. shows notification that event will end in 2 mins.
 */
const ONE_MIN_MS = 60000;

export const ADMIN_NOTIFICATION_TIME_MINUTES = 10;
const ADMIN_NOTIFICATION_TIME = ADMIN_NOTIFICATION_TIME_MINUTES * ONE_MIN_MS;
const ADMIN_NOTIFICATION_ID = "ADMIN_NOTIFICATION_ID";

export const ATTENDEE_NOTIFICATION_TIME_MINUTES = 2;
const ATTENDEE_NOTIFICATION_TIME =
  ATTENDEE_NOTIFICATION_TIME_MINUTES * ONE_MIN_MS;
const ATTENDEE_NOTIFICATION_ID = "ATTENDEE_NOTIFICATION_ID";

const EndEventNotification = () => {
  const event = useSelector(selectCurrentEvent);
  const { getCurrentTime } = useSystemClock();
  const dispatch = useAppDispatch();
  const adminTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const attendeeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const endTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const channel = useSelector(selectAnnouncementChannel);
  const sender = useSelector(selectAnnouncementSender);
  const { sendAnnouncement } = useAnnouncementActions();
  const { getTimeLimitPerEvent } = useCompanyPlanSettings();
  const {
    subscribeToCloseExtendEventMessage,
    unsubscribeFromCloseExtendEventMessage,
    closeAllExtendNotifications,
  } = useEventMessages();
  const maxTimeForEvent = getTimeLimitPerEvent();
  const isEventManager = useSelector(selectIsEventManager);
  const { t } = useI18n(["common", "event", "server"]);

  const { checkIfEventEnded } = useCheckEventStatus();

  const setAdminTimerRef = (fn: () => void, ms: number) => {
    if (adminTimer.current) clearTimeout(adminTimer.current);

    adminTimer.current = setTimeout(fn, ms);
  };
  const setAttendeeTimerRef = (fn: () => void, ms: number) => {
    if (attendeeTimer.current) clearTimeout(attendeeTimer.current);

    attendeeTimer.current = setTimeout(fn, ms);
  };
  const setEndTimerRef = (fn: () => void, ms: number) => {
    if (endTimer.current) clearTimeout(endTimer.current);

    endTimer.current = setTimeout(fn, ms);
  };

  // clean up timers when unmounting
  React.useEffect(
    () => () => {
      if (adminTimer.current) clearTimeout(adminTimer.current);

      if (attendeeTimer.current) clearTimeout(attendeeTimer.current);

      if (endTimer.current) clearTimeout(endTimer.current);
    },
    [],
  );

  React.useEffect(() => {
    subscribeToCloseExtendEventMessage(() =>
      dispatch(closeDialogNotificationsByType(ADMIN_NOTIFICATION_ID)),
    );

    return () => {
      unsubscribeFromCloseExtendEventMessage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscribeToCloseExtendEventMessage,
    unsubscribeFromCloseExtendEventMessage,
  ]);

  const sendEventAnnouncement = (message: string) => {
    sendAnnouncement(channel || "", {
      sender,
      message,
    });
  };

  /**
   * For admin only
   * Checks if end time is > than 10 mins, then set a timer to show notification at 10th min
   * we show these notification only if admin is in event when end time crosses 10 mins.
   * If admin was not present in the event during this time and comes in/refreshes the page with end time < 10 mins,
   *  we will not show the notification.
   */
  const checkEndTimeForEventManager = async () => {
    if (!event || !event.endTime || !event.startTime) return;

    const currentEventDuration = event.endTime - event.startTime;

    // If max time allowed is not unlimited and if current duration + next 30 mins is > max time allowed
    // DO not extend event
    if (
      maxTimeForEvent > -1 &&
      currentEventDuration &&
      currentEventDuration + 30 * ONE_MIN_MS > maxTimeForEvent * ONE_MIN_MS
    ) {
      return;
    }
    const serverTime = getCurrentTime();
    const timeRemaining = event.endTime - serverTime;

    // Event with end time > 10mins and  < 10mins
    // Not needed for ended event
    if (timeRemaining > 0) {
      // Calculate when to show notification based on event end time and calculated client time
      const showNotificationAt =
        event.endTime - ADMIN_NOTIFICATION_TIME - serverTime;
      const duration = moment.duration(showNotificationAt);

      // Do not set timer if event is not ending within one day
      if (duration.asHours() > 24) {
        return;
      }

      // Clear admin timer if anything is already set
      if (showNotificationAt > 0) {
        setAdminTimerRef(() => {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          showNotificationForAdmin(ADMIN_NOTIFICATION_TIME_MINUTES);
        }, showNotificationAt);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        showNotificationForAdmin(
          moment.duration(event.endTime - serverTime).minutes() + 1,
        );
      }
    }
  };

  const showNotificationForAdmin = (numberOfMinutes: number) => {
    dispatch(
      addDialogNotification({
        title: t("event:extend.title"),
        message: t("event:extend.request", { key: numberOfMinutes }),
        hideCloseButton: true,
        type: ADMIN_NOTIFICATION_ID,
        confirmText: t("event:extend.confirm"),
        dismissText: t("event:extend.cancel", { key: numberOfMinutes }),
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onDismiss: onAdminCancel,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onConfirm: onAdminExtend,
      }),
    );
  };

  const showNotificationForAttendee = async (
    numberOfMinutes: number,
    message?: string,
  ) => {
    dispatch(
      addDialogNotification({
        message: message || t("event:closing.info", { key: numberOfMinutes }),
        hideDismissButton: true,
        type: ATTENDEE_NOTIFICATION_ID,
        title: t("event:closing.info.title"),
      }),
    );
  };

  const isEventEnded = async () => {
    if (!event || !event.endTime) return;

    const serverTime = getCurrentTime();

    // If time changed after event ended, reset the notification
    const timeRemaining = event.endTime - serverTime;
    const duration = moment.duration(timeRemaining);

    // Do not set timer if event is not ending within one day
    if (duration.asHours() > 24) {
      logger.info(
        `[EndEventNotification][isEventEnded] ${event?.id} Notification not set as event is not ending today!`,
      );

      return;
    }

    if (timeRemaining > 3000) {
      setEndTimerRef(() => {
        if (checkIfEventEnded(event)) {
          if (adminTimer.current) clearTimeout(adminTimer.current);

          if (attendeeTimer.current) clearTimeout(attendeeTimer.current);
          dispatch(closeDialogNotificationsByType(ATTENDEE_NOTIFICATION_ID));
          dispatch(closeDialogNotificationsByType(ADMIN_NOTIFICATION_ID));
        }
      }, timeRemaining);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      checkWarnings();
    } else {
      if (adminTimer.current) clearTimeout(adminTimer.current);

      if (attendeeTimer.current) clearTimeout(attendeeTimer.current);
      dispatch(closeDialogNotificationsByType(ATTENDEE_NOTIFICATION_ID));
      dispatch(closeDialogNotificationsByType(ADMIN_NOTIFICATION_ID));
    }
  };

  const checkWarnings = () => {
    if (!event || !event.endTime) return;

    const showSecondNotificationAt =
      event.endTime - ATTENDEE_NOTIFICATION_TIME - getCurrentTime();

    if (showSecondNotificationAt && showSecondNotificationAt > 0) {
      // second notification
      setAttendeeTimerRef(() => {
        showNotificationForAttendee(ATTENDEE_NOTIFICATION_TIME_MINUTES);
      }, showSecondNotificationAt);
    }
  };

  React.useEffect(() => {
    isEventEnded();

    if (isEventManager) {
      checkEndTimeForEventManager();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventManager, event?.endTime]);

  const onAdminCancel = async () => {
    closeAllExtendNotifications();
    addSuccessNotification({
      message: t("event:end.soon"),
    });
    dispatch(closeDialogNotificationsByType(ADMIN_NOTIFICATION_ID));
  };
  /**
   * Make api call to extend event
   * Show result to admin
   * send an announcement to all others
   */
  const onAdminExtend = async () => {
    const resp = await extendEventEndTime({ event: event?.id, extendBy: 30 });

    if (resp.isSuccess) {
      closeAllExtendNotifications();
      addSuccessNotification({ message: t("event:event.extend") });
      sendEventAnnouncement(t("event:post.event.extend"));
      dispatch(closeDialogNotificationsByType(ATTENDEE_NOTIFICATION_ID));
    } else {
      addErrorNotification({
        message: t(resp.message || "") || t("event:try.later"),
      });
    }
    dispatch(closeDialogNotificationsByType(ADMIN_NOTIFICATION_ID));
  };

  return null;
};

export default EndEventNotification;
